import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import {
  adminDispatchEmsRemindersApi,
  downloadEmsRemindersCsvApi,
  fetchEmsRemindersApi,
  sendEmsRemindersEmailApi,
} from "../../lib/api";

export default function EMSReminders() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [dispatchingAll, setDispatchingAll] = useState(false);
  const [daysAhead, setDaysAhead] = useState(14);
  const [data, setData] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [filterModule, setFilterModule] = useState("all");
  const role = localStorage.getItem("role");

  const loadData = async (days = daysAhead) => {
    try {
      setLoading(true);
      const payload = await fetchEmsRemindersApi(days);
      setData(payload || null);
    } catch (err) {
      toast.error(err?.message || "Failed to load reminders");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(14);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reminders = useMemo(() => (Array.isArray(data?.reminders) ? data.reminders : []), [data]);
  const summary = data?.summary || { total_reminders: 0, high: 0, medium: 0, owners_affected: 0 };

  const ownerOptions = useMemo(() => {
    const set = new Set(reminders.map((r) => String(r.owner || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [reminders]);

  const moduleOptions = useMemo(() => {
    const set = new Set(reminders.map((r) => String(r.module_name || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [reminders]);

  const filtered = useMemo(() => {
    return reminders.filter((r) => {
      if (filterSeverity !== "all" && String(r.severity || "") !== filterSeverity) return false;
      if (filterOwner === "__unassigned__") {
        if (String(r.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(r.owner || "") !== filterOwner) {
        return false;
      }
      if (filterModule !== "all" && String(r.module_name || "") !== filterModule) return false;
      return true;
    });
  }, [reminders, filterSeverity, filterOwner, filterModule]);

  const handleRefresh = async () => {
    await loadData(daysAhead);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await downloadEmsRemindersCsvApi(daysAhead);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ems_reminders_${daysAhead}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Reminders exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export reminders");
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmails = async () => {
    try {
      setSending(true);
      const res = await sendEmsRemindersEmailApi({ days_ahead: daysAhead });
      toast.success(res?.message || "Reminder email sent");
    } catch (err) {
      toast.error(err?.message || "Failed to send reminder emails");
    } finally {
      setSending(false);
    }
  };

  const handleDispatchAll = async () => {
    try {
      setDispatchingAll(true);
      const res = await adminDispatchEmsRemindersApi({ force: true });
      toast.success(res?.message || "Dispatch completed");
    } catch (err) {
      toast.error(err?.message || "Failed to dispatch reminders");
    } finally {
      setDispatchingAll(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>Reminders & Escalations</h1>
          <p className="subtitle">Overdue and due-soon items with owner-level workload visibility.</p>
        </div>
        <div className="dashboard-header-actions">
          <label className="settings-field" style={{ minWidth: 130 }}>
            <span>Days ahead</span>
            <input
              type="number"
              min="1"
              max="90"
              step="1"
              value={daysAhead}
              onChange={(e) => setDaysAhead(e.target.value)}
              disabled={loading}
            />
          </label>
          <button className="btn-secondary" onClick={handleRefresh} disabled={loading}>
            Refresh
          </button>
          <button className="btn-secondary" onClick={handleExport} disabled={loading || exporting}>
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
          <button className="btn-primary" onClick={handleSendEmails} disabled={loading || sending}>
            {sending ? "Sending..." : "Send Email Reminders"}
          </button>
          {role === "admin" ? (
            <button className="btn-secondary" onClick={handleDispatchAll} disabled={loading || dispatchingAll}>
              {dispatchingAll ? "Dispatching..." : "Dispatch All Clients"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="summary-row">
        <div className="kpi-card">
          <div className="kpi-label">Total reminders</div>
          <h2>{loading ? "-" : Number(summary.total_reminders || 0)}</h2>
          <div className="kpi-sub">Overdue + due soon</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">High severity</div>
          <h2>{loading ? "-" : Number(summary.high || 0)}</h2>
          <div className="kpi-sub">Overdue items</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Owners affected</div>
          <h2>{loading ? "-" : Number(summary.owners_affected || 0)}</h2>
          <div className="kpi-sub">Distribution of workload</div>
        </div>
      </div>

      <div className="filter-bar" style={{ gridTemplateColumns: "170px 200px 220px 1fr" }}>
        <label className="settings-field">
          <span>Severity</span>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
            <option value="all">all</option>
            <option value="high">high</option>
            <option value="medium">medium</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span>Module</span>
          <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)}>
            <option value="all">all</option>
            {moduleOptions.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>

        <span className="filter-count">{filtered.length} of {reminders.length} shown</span>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Severity</th>
              <th>Module</th>
              <th>Title</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Due date</th>
              <th>Days to due</th>
              <th>Evidence</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="table-empty">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="table-empty">No reminders in this range.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.reminder_id}>
                  <td>{r.severity || "-"}</td>
                  <td>{r.module_name || "-"}</td>
                  <td>{r.title || "-"}</td>
                  <td>{r.owner || "-"}</td>
                  <td>{r.status || "-"}</td>
                  <td>{r.due_date || "-"}</td>
                  <td>{Number(r.days_to_due || 0)}</td>
                  <td><EvidenceReferenceView value={r.evidence} /></td>
                  <td><Link to={r.route || "/dashboard"}>Open module</Link></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
